import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';

interface Theme {
  id: string;
  name: string;
  description?: string;
  color?: string;
  bookCount?: number;
  parentId?: string;
  path?: string;
  level?: number;
  children?: Theme[];
  displayName?: string;
}

interface ThemeSelectorProps {
  value: string[];
  onChange: (themes: string[]) => void;
  className?: string;
}

export function ThemeSelector({ value, onChange, className }: ThemeSelectorProps) {
  const { token } = useAuth();
  const [themes, setThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadThemes();
  }, []);

  async function loadThemes() {
    try {
      const response = await fetch('/api/admin/themes', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // 使用扁平化的主题列表
        setThemes(data.flatList || []);
      }
    } catch (err) {
      console.error('Failed to load themes:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleThemeToggle(themeId: string) {
    if (value.includes(themeId)) {
      onChange(value.filter(id => id !== themeId));
    } else {
      onChange([...value, themeId]);
    }
  }

  if (loading) {
    return <div className="text-gray-500">Loading themes...</div>;
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        选择主题
      </label>
      <div className="max-h-60 overflow-y-auto p-3 border rounded-lg bg-gray-50">
        {themes.map(theme => (
          <label
            key={theme.id}
            className={`flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded transition-colors ${
              (theme.level || 0) > 0 ? 'ml-6' : ''
            }`}
          >
            <input
              type="checkbox"
              value={theme.id}
              checked={value.includes(theme.id)}
              onChange={() => handleThemeToggle(theme.id)}
              className="rounded border-gray-300"
            />
            <div className="flex items-center gap-2 flex-1">
              {theme.color && (
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: theme.color }}
                />
              )}
              <span
                className={`text-sm ${(theme.level || 0) > 0 ? 'text-gray-600' : 'text-gray-900'}`}
                title={theme.path || theme.name}
              >
                {theme.displayName || theme.name}
              </span>
              {(theme.level || 0) > 0 && (
                <span className="text-xs text-gray-400 ml-1">
                  L{theme.level}
                </span>
              )}
            </div>
          </label>
        ))}
      </div>
      {themes.length === 0 && (
        <div className="text-sm text-gray-500 text-center py-2">
          暂无主题，请先添加主题
        </div>
      )}
      <div className="text-xs text-gray-500 mt-1">
        已选择 {value.length} 个主题
      </div>
    </div>
  );
}